import { FileInterface, RichText } from "../types/RecordInterfaces";
import { callFunctionIfExists } from "./GeneralUtils";
import TenantAPI from "../api/api/TenantAPI";
import { AppConfirm } from "./PopupUtil";
import StrUtils from "./StrUtils";

interface DeleteFileBlobProps {
  filePath: string;
  recordID: string;
  moduleAPIName: string;
  fieldAPIName: string;
  subitemID?: string;
  onSuccess?: (response: any) => void;
  onError?: (e: any) => void;
}

export const deleteFileBlobStorage = ({
  filePath,
  recordID,
  moduleAPIName,
  fieldAPIName,
  subitemID,
  onSuccess,
  onError,
}: DeleteFileBlobProps) => {
  AppConfirm({
    title: "Confirm Deletion",
    text: "Are you sure you want to delete your Attachment? This action cannot be undone.",
  })
    .then((confirmation) => {
      if (!confirmation.isConfirmed) throw Error("Deletion Cancelled");
      TenantAPI.deleteFiles({
        type: "blobName",
        blobName: filePath,
        recordID,
        moduleAPIName,
        fieldAPIName,
        subitemID,
      });
    })
    .then((response) => callFunctionIfExists(onSuccess, response))
    .catch((e) => callFunctionIfExists(onError, e));
};

export const downloadFile = async (
  fileSource: FileInterface,
  asBlob?: boolean
) => {
  try {
    if (!fileSource.URL) throw Error("No File URL found");
    const response = await fetch(fileSource.URL);
    if (response.status !== 200) {
      throw new Error(
        `Unable to download file. HTTP status: ${response.status}`
      );
    }
    const blob = await response.blob();
    if (asBlob) return blob;
    const downloadLink = document.createElement("a");
    downloadLink.href = URL.createObjectURL(blob);
    downloadLink.download = fileSource.Name;
    document.body.appendChild(downloadLink);
    downloadLink.click();
    URL.revokeObjectURL(downloadLink.href);
    document.body.removeChild(downloadLink);
  } catch (error: any) {
    console.error(error.message);
  }
};

export async function handleFileTransform(
  file: FileInterface,
  isPublic: boolean
) {
  let fileURL = isPublic ? file.PublicURL : file?.URL;
  if (!fileURL) {
    fileURL = await TenantAPI.getFileURL(file.File).then((data) => {
      return data.data.data.url;
    });
  }
  file.URL = fileURL;

  const newFile: FileInterface = {
    ...file,
  };
  const blob = await downloadFile(file, true);

  if (!blob) {
    throw new Error("Unable to download file");
  }
  const myFile = new File([blob], file.Name, { type: blob.type });

  const {
    data: {
      data: { paths, recordID },
      url,
    },
  } = await TenantAPI.uploadFile({
    file: myFile,
    sources: undefined,
    usePublicContainer: isPublic,
  });
  newFile.File = paths[0];
  newFile.RecordID = recordID;

  if (isPublic) newFile.PublicURL = url;
  delete newFile.URL;

  return newFile;
}

export async function copyRichTextFiles(payload: RichText): Promise<RichText> {
  const richText = payload.RichText;
  const regex = new RegExp(/src\s*=\s*"(.+?)"/g);
  const matches = [...richText.matchAll(regex)];
  const originalNames = [];
  const currentFiles = matches.map((match) => {
    const name = match[1].split("/uploaded_files/")[1]?.split("?")[0];
    const parsedURL = match[1].replace(/&amp;/g, "&");
    const newFile: FileInterface = {
      id: StrUtils.uuidv4(),
      Name: `Copy_${name}`,
      File: `uploaded_files/${name}`,
      URL: parsedURL,
      AltText: "rich-text-image",
      Caption: "",
    };
    originalNames.push({ original: name, copy: newFile.Name });
    return newFile;
  });

  const promises = currentFiles.map(async (file) => {
    const newFile = await handleFileTransform(file, false);
    const newURL = await TenantAPI.getFileURL(newFile.File, 31536000);
    newFile.URL = newURL.data.data.url;
    return newFile;
  });
  const newFiles = await Promise.all(promises);

  const newRichText = richText.replace(regex, (match) => {
    const name = match.split("/uploaded_files/")[1]?.split("?")[0];
    const foundFile = newFiles.find((file) => file.Name === `Copy_${name}`);

    return `src="${foundFile?.URL}"`;
  });

  return { RichText: newRichText, PlainText: payload.PlainText };
}

export function downloadJSON(data: any, fileName: string) {
  try {
    const jsonString = JSON.stringify(data, null, 2);
    const blob = new Blob([jsonString], { type: "application/json" });

    // Step 2: Create a link with URL and download attribute
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `${fileName}.json`;

    // Step 3: Programmatically click the link to trigger the download
    link.click();

    // Cleanup
    URL.revokeObjectURL(link.href);
  } catch {}
}
